
import { useMemo } from 'react';

interface ScheduleItem {
  id: number;
  date: string;
  day: string;
  time: string;
  doorOpen: string;
  venue: string;
  artist: string;
  title: string;
  genre: string;
  price: number;
  ticketUrl: string;
  status: string;
  description: string;
}

interface ScheduleCalendarProps {
  schedules: ScheduleItem[];
  selectedMonth: string;
  onEventClick: (item: ScheduleItem) => void;
}

export default function ScheduleCalendar({ schedules, selectedMonth, onEventClick }: ScheduleCalendarProps) {
  const year = parseInt(selectedMonth.split('-')[0], 10);
  const month = parseInt(selectedMonth.split('-')[1], 10);

  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const startDayOfWeek = firstDay.getDay();
    const totalDays = lastDay.getDate();

    const days: { date: number | null; events: ScheduleItem[] }[] = [];

    for (let i = 0; i < startDayOfWeek; i++) {
      days.push({ date: null, events: [] });
    }

    for (let d = 1; d <= totalDays; d++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const events = schedules.filter((s) => s.date === dateStr);
      days.push({ date: d, events });
    }

    const remainder = days.length % 7;
    if (remainder > 0) {
      for (let i = 0; i < 7 - remainder; i++) {
        days.push({ date: null, events: [] });
      }
    }

    return days;
  }, [schedules, year, month]);

  const weekDays = ['日', '月', '火', '水', '木', '金', '土'];

  const genreColorMap: Record<string, string> = {
    'ロック': 'bg-rose-100 text-rose-700',
    'ジャズ': 'bg-amber-100 text-amber-700',
    'エレクトロニカ': 'bg-cyan-100 text-cyan-700',
    'アコースティック': 'bg-emerald-100 text-emerald-700',
    'R&B/ソウル': 'bg-orange-100 text-orange-700',
    'シンガーソングライター': 'bg-teal-100 text-teal-700',
    'ブルース': 'bg-stone-200 text-stone-700',
    'ヒップホップ': 'bg-pink-100 text-pink-700',
    'フォーク': 'bg-lime-100 text-lime-700',
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Week Header */}
      <div className="grid grid-cols-7 border-b border-gray-200">
        {weekDays.map((wd, i) => (
          <div
            key={wd}
            className={`py-3 text-center text-xs font-semibold ${
              i === 0 ? 'text-rose-500' : i === 6 ? 'text-teal-600' : 'text-gray-500'
            }`}
          >
            {wd}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7">
        {calendarDays.map((cell, idx) => {
          const dayOfWeek = idx % 7;
          const isSunday = dayOfWeek === 0;
          const isSaturday = dayOfWeek === 6;

          return (
            <div
              key={idx}
              className={`min-h-[110px] border-b border-r border-gray-100 p-2 ${
                cell.date === null ? 'bg-gray-50/50' : 'bg-white'
              } ${idx % 7 === 6 ? 'border-r-0' : ''}`}
            >
              {cell.date !== null && (
                <>
                  <span
                    className={`text-xs font-semibold ${
                      isSunday ? 'text-rose-500' : isSaturday ? 'text-teal-600' : 'text-gray-700'
                    }`}
                  >
                    {cell.date}
                  </span>
                  <div className="mt-1 space-y-1">
                    {cell.events.map((ev) => (
                      <button
                        key={ev.id}
                        onClick={() => onEventClick(ev)}
                        className={`w-full text-left px-1.5 py-1 rounded text-[10px] leading-tight font-medium truncate cursor-pointer hover:opacity-80 transition-opacity ${
                          genreColorMap[ev.genre] || 'bg-gray-100 text-gray-700'
                        }`}
                        title={`${ev.title} - ${ev.artist}`}
                      >
                        <span className="block truncate">{ev.time} {ev.title}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
